import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, CheckCircle2, FileText, FileSignature, Receipt, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, downloadPdf } from "@/lib/api";
import { formatCOP, inputCls } from "@/components/common";
import SignaturePad from "@/components/SignaturePad";

const KIND = {
  quote: { label: "Cotización", icon: FileText, cta: "Aceptar cotización" },
  contract: { label: "Contrato", icon: FileSignature, cta: "Firmar contrato" },
  cc: { label: "Cuenta de cobro", icon: Receipt, cta: "" },
};

const fmtDate = (iso) => (iso ? new Date(iso.slice(0, 10) + "T12:00:00").toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" }) : "");

const Brand = ({ agency }) => (
  <div className="flex items-center gap-3">
    {agency.logo_data_url ? <img src={agency.logo_data_url} alt={agency.brand} className="h-10 object-contain"/> : (
      <div className="leading-none">
        <div className="text-2xl font-extrabold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>{(agency.brand || "").split(" ")[0].toUpperCase()}</div>
        <div className="text-[10px] font-bold tracking-[0.25em] text-[#F97316]">{(agency.brand || "").split(" ").slice(1).join(" ").toUpperCase()}</div>
      </div>
    )}
  </div>
);

const ItemsTable = ({ items, withName }) => (
  <div className="rounded-xl overflow-hidden border border-[#262A33]">
    <table className="w-full text-sm">
      <thead><tr className="bg-[#0B0D12] text-[#9CA3AF] text-xs"><th className="text-left px-4 py-2.5 font-medium">Descripción</th><th className="text-right px-4 py-2.5 font-medium">Cant.</th><th className="text-right px-4 py-2.5 font-medium">Vlr. unitario</th><th className="text-right px-4 py-2.5 font-medium">Total</th></tr></thead>
      <tbody>
        {items.map((it, i) => (
          <tr key={i} className="border-t border-[#262A33]">
            <td className="px-4 py-3 text-[#F5F5F7]">{withName ? <><div className="font-medium">{it.name}</div>{it.description && <div className="text-xs text-[#9CA3AF]">{it.description}</div>}</> : it.description}</td>
            <td className="px-4 py-3 text-right text-[#9CA3AF]">{it.qty}</td>
            <td className="px-4 py-3 text-right text-[#9CA3AF]">{formatCOP(it.unit_price)}</td>
            <td className="px-4 py-3 text-right text-[#F5F5F7] font-medium">{formatCOP(Number(it.qty) * Number(it.unit_price))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AcceptForm = ({ token, kind, onDone }) => {
  const [name, setName] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!sig) { toast.error("Dibuja tu firma para continuar"); return; }
    setBusy(true);
    try { await api.post(`/public/docs/${token}/accept`, { name, id_number: idNumber, signature_data_url: sig }); toast.success("¡Documento aceptado!"); onDone(); }
    catch (err) { toast.error(err?.response?.data?.detail || "No se pudo registrar la aceptación"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="accept-form" className="bg-[#15171E] border border-[#F97316]/30 rounded-2xl p-6 space-y-4">
      <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#F97316]"/><h3 className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>{KIND[kind].cta}</h3></div>
      <p className="text-xs text-[#9CA3AF]">Al firmar, confirmas que has leído y aceptas los términos de este documento. Se registrará tu nombre, documento, fecha y hora.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="accept-name" required placeholder="Nombre completo *" className={inputCls} value={name} onChange={e => setName(e.target.value)}/>
        <input data-testid="accept-id" required placeholder="Cédula / NIT *" className={inputCls} value={idNumber} onChange={e => setIdNumber(e.target.value)}/>
      </div>
      <SignaturePad onChange={setSig}/>
      <button data-testid="accept-submit" disabled={busy} type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-xl px-4 py-3 text-sm transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>} {KIND[kind].cta}
      </button>
    </form>
  );
};

export default function PublicDoc() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get(`/public/docs/${token}`).then(r => setDoc(r.data)).catch(() => setErr("Este enlace no es válido o el documento ya no está disponible."));
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  if (err) return <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center text-[#9CA3AF] p-6 text-center">{err}</div>;
  if (!doc) return <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#F97316] animate-spin"/></div>;

  const k = KIND[doc.kind]; const Icon = k.icon;
  const download = async () => { try { await downloadPdf(`/public/docs/${token}/pdf`, `${doc.number}.pdf`); } catch { toast.error("No se pudo descargar"); } };

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F5F7]">
      <header className="border-b border-[#262A33] bg-[#0B0D12]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-4"><Brand agency={doc.agency}/>
          <button data-testid="public-download-pdf" onClick={download} className="inline-flex items-center gap-2 border border-[#262A33] hover:bg-[#15171E] rounded-xl px-3 py-2 text-sm font-medium"><Download className="w-4 h-4"/> PDF</button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-8 md:py-12 space-y-6 fade-in">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-[#F97316]"><Icon className="w-4 h-4"/> {k.label.toUpperCase()}</div>
            <h1 data-testid="public-doc-number" className="text-3xl md:text-4xl font-bold mt-2" style={{ fontFamily: "Manrope, sans-serif" }}>{doc.number}</h1>
            <p className="text-sm text-[#9CA3AF] mt-1">Para <span className="text-[#F5F5F7]">{doc.client_name}</span> · {fmtDate(doc.issued_at)}{doc.valid_until ? ` · Válida hasta ${fmtDate(doc.valid_until)}` : ""}</p>
          </div>
          <div className="bg-[#15171E] border border-[#262A33] rounded-2xl px-5 py-3 text-right"><div className="text-[11px] text-[#9CA3AF]">Total</div><div className="text-2xl font-bold text-[#F97316]" style={{ fontFamily: "Manrope, sans-serif" }}>{formatCOP(doc.total)}</div></div>
        </div>

        {doc.acceptance && (
          <div data-testid="accepted-banner" className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/25 rounded-2xl p-4">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0"/>
            <div className="text-sm"><span className="font-semibold text-emerald-300">{doc.kind === "contract" ? "Firmado" : "Aceptado"} electrónicamente</span> por {doc.acceptance.name} (C.C. {doc.acceptance.id_number}) el {fmtDate(doc.acceptance.accepted_at)}.</div>
          </div>
        )}

        <section className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 space-y-5">
          {doc.kind === "quote" && (<>
            {doc.title && <h2 className="text-lg font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>{doc.title}</h2>}
            {doc.intro && <p className="text-sm text-[#9CA3AF] whitespace-pre-line">{doc.intro}</p>}
            <ItemsTable items={doc.items} withName/>
            {doc.discount > 0 && <div className="text-right text-sm text-[#9CA3AF]">Descuento: - {formatCOP(doc.discount)}</div>}
            {doc.notes && <div className="border-l-2 border-[#F97316] pl-4 text-xs text-[#9CA3AF] whitespace-pre-line"><div className="font-bold text-[#F97316] mb-1">CONDICIONES</div>{doc.notes}</div>}
          </>)}
          {doc.kind === "contract" && (<>
            <div><div className="text-[11px] font-bold tracking-wider text-[#F97316]">OBJETO</div><p className="text-sm mt-1">{doc.object}</p></div>
            {doc.scope_items?.length > 0 && <div><div className="text-[11px] font-bold tracking-wider text-[#F97316]">ALCANCE</div><ol className="list-decimal list-inside text-sm text-[#D1D5DB] mt-1 space-y-1">{doc.scope_items.map((s, i) => <li key={i}>{s}</li>)}</ol></div>}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <div className="bg-[#0B0D12] rounded-xl p-3"><div className="text-[11px] text-[#9CA3AF]">Inicio</div>{fmtDate(doc.start_date)}</div>
              <div className="bg-[#0B0D12] rounded-xl p-3"><div className="text-[11px] text-[#9CA3AF]">Plazo</div>{doc.duration_text || `${doc.duration_months} meses`}</div>
              <div className="bg-[#0B0D12] rounded-xl p-3 col-span-2 sm:col-span-1"><div className="text-[11px] text-[#9CA3AF]">Valor</div>{formatCOP(doc.total)}</div>
            </div>
            <div><div className="text-[11px] font-bold tracking-wider text-[#F97316]">FORMA DE PAGO</div><p className="text-sm text-[#D1D5DB] mt-1 whitespace-pre-line">{doc.payment_terms}</p></div>
            <p className="text-xs text-[#6B7280]">El texto completo de las cláusulas está en el PDF. Descárgalo con el botón superior antes de firmar.</p>
          </>)}
          {doc.kind === "cc" && (<>
            <ItemsTable items={doc.items} withName={false}/>
            <div className="border-l-2 border-[#F97316] pl-4 text-sm"><div className="text-[11px] font-bold tracking-wider text-[#F97316] mb-1">INFORMACIÓN DE PAGO</div>
              <div>{doc.bank.bank_name} · {doc.bank.bank_account_type} No. <span className="font-mono">{doc.bank.bank_account}</span></div><div className="text-[#9CA3AF]">Titular: {doc.bank.bank_holder}</div></div>
          </>)}
        </section>

        {doc.can_accept && !doc.acceptance && <AcceptForm token={token} kind={doc.kind} onDone={load}/>}

        <footer className="text-center text-[11px] text-[#6B7280] pt-4">{doc.agency.name} · NIT {doc.agency.nit} · {doc.agency.email} · {doc.agency.phone}</footer>
      </main>
    </div>
  );
}
